import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { X, Scale, Trash2, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { siteUrl } from "@/lib/site";
import { useContact } from "@/lib/contact";
import { track } from "@/lib/track";
import { useFeatureFlags } from "@/lib/featureFlags";
import { activeOpts, fmtDate, priceLabel } from "@/lib/offerShared";
import { useOfferCompare } from "@/context/CompareContext";

const NA = "غير مذكور";

// يطابق خيارًا سعريًا ساريًا حسب الليالي والفترة المختارتين (فترة "all" = أرخص خيار سارٍ)
const matchOption = (offer, nights, periodLabel) => {
  const opts = activeOpts(offer).filter((o) => !o.askPrice && (o.priceBHD || o.priceSAR));
  const byNights = nights ? opts.filter((o) => Number(o.nights) === Number(nights)) : opts;
  if (periodLabel && periodLabel !== "all") {
    return byNights.find((o) => o.periodLabel === periodLabel) || null;
  }
  return [...byNights].sort((a, b) => (a.priceBHD || Infinity) - (b.priceBHD || Infinity))[0] || null;
};

const Row = ({ label, cells, testid }) => (
  <tr className="border-b border-slate-100" data-testid={testid}>
    <th className="sticky start-0 bg-white px-3 py-3 text-start text-xs font-bold text-slate-500 align-top w-28">{label}</th>
    {cells.map((c, i) => <td key={i} className="px-3 py-3 text-sm text-slate-800 align-top">{c || <span className="text-slate-400 text-xs">{NA}</span>}</td>)}
  </tr>
);

const CompareModal = ({ onClose }) => {
  const { items, clear, toggle } = useOfferCompare();
  const CONTACT = useContact();
  const [details, setDetails] = useState({});
  const [nights, setNights] = useState("");
  const [period, setPeriod] = useState("all");

  useEffect(() => {
    let alive = true;
    Promise.all(items.map((o) => api.get(`/offers/${o.slug}`).then(({ data }) => data).catch(() => null)))
      .then((arr) => { if (alive) setDetails(Object.fromEntries(arr.filter(Boolean).map((d) => [d.id, d]))); });
    return () => { alive = false; };
  }, [items]);

  useEffect(() => { track("compare_open", String(items.length)); }, [items.length]);

  const nightsChoices = useMemo(() => {
    const sets = items.map((o) => new Set(activeOpts(o).map((x) => Number(x.nights)).filter(Boolean)));
    if (!sets.length) return [];
    const common = [...sets[0]].filter((n) => sets.every((s) => s.has(n)));
    return (common.length ? common : [...new Set(sets.flatMap((s) => [...s]))]).sort((a, b) => a - b);
  }, [items]);

  const periodChoices = useMemo(() => {
    const labels = new Set();
    items.forEach((o) => activeOpts(o).forEach((x) => x.periodLabel && labels.add(x.periodLabel)));
    return [...labels];
  }, [items]);

  const effNights = nights || nightsChoices[0] || "";
  const matched = items.map((o) => matchOption(details[o.id] || o, effNights, period));

  const waUrl = useMemo(() => {
    const lines = [
      "أحتاج مساعدة في اختيار عرض — دليل المسافر", "",
      `أقارن بين ${items.length} عروض:`,
    ];
    items.forEach((o, i) => {
      const m = matched[i];
      lines.push(`${i + 1}) ${o.title}`);
      lines.push(`   الخيار: ${m ? `${m.nights} ليالٍ — ${m.periodLabel || "الفترة السارية"} — ${priceLabel(m)}` : NA}`);
      lines.push(`   رابط: ${siteUrl(`/offers/${o.slug}`)}`);
    });
    lines.push("", `توحيد المقارنة: الليالي ${effNights || NA} — الفترة: ${period === "all" ? "أرخص فترة سارية لكل عرض" : period}`);
    lines.push("", "أرغب في تأكيد السعر الحالي والتوفر لهذه العروض.", "هذا استفسار مقارنة وليس حجزًا مؤكدًا.");
    const base = (CONTACT.whatsapp || "https://wa.me/97313303332").split("?")[0];
    return `${base}?text=${encodeURIComponent(lines.join("\n"))}`;
  }, [items, matched, effNights, period, CONTACT]);

  if (!items.length) return null;

  return (
    <div className="fixed inset-0 z-[95] bg-black/40 backdrop-blur-sm p-3 sm:p-6 flex items-center justify-center" onClick={onClose} data-testid="compare-modal">
      <div className="max-h-[90vh] w-full max-w-5xl overflow-auto rounded-3xl border border-slate-200 bg-white p-5 sm:p-7" onClick={(e) => e.stopPropagation()}>
        <div className="mb-5 flex items-start justify-between gap-3">
          <div>
            <h2 className="font-display text-xl font-bold text-[#1A1A1E]">مقارنة العروض</h2>
            <p className="mt-1 text-xs text-slate-500">قارن حتى 3 عروض — «غير مذكور» تعني أن البيان غير موثق في العرض</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={clear} data-testid="compare-clear" className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-4 py-2 text-xs font-bold text-slate-500 hover:text-red-600 hover:border-red-300"><Trash2 className="h-3.5 w-3.5" /> مسح الكل</button>
            <button onClick={onClose} aria-label="إغلاق" data-testid="compare-close" className="h-9 w-9 rounded-full border border-slate-200 text-slate-500 hover:text-[#C80000]"><X className="h-4 w-4 mx-auto" /></button>
          </div>
        </div>

        {/* توحيد الليالي والفترة */}
        <div className="mb-5 flex flex-wrap items-center gap-3 rounded-2xl border border-slate-200 bg-[#F8F9FA] p-4" data-testid="compare-unify">
          <span className="text-xs font-bold text-slate-600">وحّد المقارنة:</span>
          <div className="flex flex-wrap gap-2">
            {nightsChoices.map((n) => (
              <button key={n} type="button" onClick={() => setNights(String(n))} data-testid={`compare-nights-${n}`}
                className={`rounded-full px-4 py-2 text-xs font-bold border transition-colors ${String(effNights) === String(n) ? "bg-[#C80000] border-[#C80000] text-white" : "border-slate-300 text-slate-600 hover:border-[#C80000]/50"}`}>
                {n} ليالٍ
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => setPeriod("all")} data-testid="compare-period-all"
              className={`rounded-full px-4 py-2 text-xs font-bold border transition-colors ${period === "all" ? "bg-[#C80000] border-[#C80000] text-white" : "border-slate-300 text-slate-600 hover:border-[#C80000]/50"}`}>
              أرخص فترة سارية
            </button>
            {periodChoices.map((p) => (
              <button key={p} type="button" onClick={() => setPeriod(p)} data-testid={`compare-period-${p}`}
                className={`rounded-full px-4 py-2 text-xs font-bold border transition-colors ${period === p ? "bg-[#C80000] border-[#C80000] text-white" : "border-slate-300 text-slate-600 hover:border-[#C80000]/50"}`}>
                {p}
              </button>
            ))}
          </div>
        </div>

        <div className="overflow-x-auto rounded-2xl border border-slate-200" data-testid="compare-table">
          <table className="w-full min-w-[560px] text-right">
            <thead>
              <tr className="bg-[#F8F9FA] border-b border-slate-200">
                <th className="sticky start-0 bg-[#F8F9FA] w-28 px-3 py-3" />
                {items.map((o) => (
                  <th key={o.id} className="px-3 py-3 align-top min-w-44">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        {o.image?.file && <img src={o.image.file} alt={o.title} className="mb-2 h-20 w-full rounded-xl object-cover border border-slate-200" />}
                        <p className="font-bold text-sm text-[#1A1A1E] leading-snug">{o.title}</p>
                      </div>
                      <button onClick={() => toggle(o)} aria-label={`إزالة ${o.title} من المقارنة`} data-testid={`compare-remove-${o.slug}`} className="h-7 w-7 shrink-0 rounded-full border border-slate-200 text-slate-400 hover:text-red-600 flex items-center justify-center"><X className="h-3.5 w-3.5" /></button>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <Row label="السعر" testid="compare-row-price" cells={items.map((o, i) => matched[i] ? (
                <span key={o.id}>
                  <b className="text-[#C80000]">{priceLabel(matched[i])}</b>
                  <span className="block mt-1 text-[11px] text-slate-400">إجمالي لشخصين — السعر قابل للتغيير ويخضع للتوفر</span>
                </span>
              ) : null)} />
              <Row label="الفترة" testid="compare-row-period" cells={items.map((o, i) => matched[i]?.periodLabel || (period !== "all" ? undefined : "الفترة السارية"))} />
              <Row label="الليالي" testid="compare-row-nights" cells={items.map((o, i) => matched[i]?.nights ? `${matched[i].nights} ليالٍ` : undefined)} />
              <Row label="فئة الإقامة" testid="compare-room" cells={items.map((o) => {
                const d = details[o.id];
                const rooms = d?.stays?.[0]?.roomTypes?.map((r) => r.name).filter(Boolean).join("، ");
                return rooms || undefined;
              })} />
              <Row label="الوجبات" testid="compare-meals" cells={items.map((o) => (details[o.id]?.stays?.[0]?.mealPlan || o.mealPlan || undefined))} />
              <Row label="الانتقالات" testid="compare-transfer" cells={items.map((o) => {
                const t = details[o.id]?.transfers?.[0];
                return t ? `${t.mode}${t.privateShared ? ` (${t.privateShared === "private" ? "خاص" : "مشترك"})` : ""}` : undefined;
              })} />
              <Row label="المميزات الموثقة" testid="compare-perks" cells={items.map((o) => {
                const inc = (details[o.id]?.included || []).filter((x) => !/^(انتقالات المنتجع|جميع الضرائب|نظام الوجبات)/.test(x));
                return inc.length ? <ul className="space-y-1 text-xs leading-relaxed">{inc.map((x, xi) => <li key={xi}>• {x}</li>)}</ul> : undefined;
              })} />
              <Row label="التفاصيل" testid="compare-links" cells={items.map((o) => (
                <Link key={o.id} to={`/offers/${o.slug}`} data-testid={`compare-link-${o.slug}`} className="inline-flex items-center gap-1.5 text-xs font-bold text-[#C80000] hover:underline">
                  صفحة العرض <ExternalLink className="h-3 w-3" />
                </Link>
              ))} />
            </tbody>
          </table>
        </div>

        <a href={waUrl} target="_blank" rel="noopener noreferrer" onClick={() => track("compare_wa_click", String(items.length))}
          data-testid="compare-help-wa"
          className="mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-[#25D366] py-3.5 text-sm font-bold text-white hover:bg-[#1eb857] transition-colors">
          ساعدني أختار — أرسل المقارنة عبر واتساب
        </a>
        <p className="mt-2 text-center text-[11px] text-slate-500">يفتح واتساب برسالة تضم العروض وخياراتها وروابطها — الإرسال بيدك، وهذا استفسار وليس حجزًا مؤكدًا.</p>
      </div>
    </div>
  );
};

export const CompareTray = () => {
  const { items, open, setOpen, toggle } = useOfferCompare();
  const { pathname } = useLocation();
  const flags = useFeatureFlags();
  if (!flags.offersV2) return null;
  if (pathname.startsWith("/admin")) return null;
  if (!items.length) return null;
  return (
    <>
      <div className="fixed bottom-5 end-5 z-40" data-testid="compare-tray">
        <button onClick={() => setOpen(true)} data-testid="compare-open"
          className="inline-flex items-center gap-2 rounded-full bg-[#1A1A1E] px-5 py-3 text-xs font-bold text-white shadow-2xl hover:bg-[#C80000] transition-colors">
          <Scale className="h-4 w-4" />
          قارن العروض ({items.length}/3)
        </button>
      </div>
      {open && <CompareModal onClose={() => setOpen(false)} />}
    </>
  );
};
